import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useDistributorSettingsContext } from '../context/DistributorSettingsContext';
import { PutProductDistributorFulfillmentType } from '../../ProductSettings/hooks/useFetchProductSettingsDistributorOverrides';
import { SkuFulfillmentMethod } from '../../../Enums/SkuFulfillmentMethod';
import { toYyyyMmDdInput, isSunday, maxInMarketDate, getEffectiveInMarketDate } from '../../ProductSettings/utils/inMarketDate';

export const SkuEndInMarketDatePicker = ({ row, handleUpdateRow }) => {
    const { showInMarketDateError } = useDistributorSettingsContext();
    const isReplenishable = row.skuFulfillmentType?.id === SkuFulfillmentMethod.REPLENISHABLE;
    const isOrderOnly = row.skuFulfillmentType?.id === SkuFulfillmentMethod.ORDER_ONLY;
    const itemEndInMarketDate = toYyyyMmDdInput(row.endInMarketDateDefault);
    const effectiveInMarketDate = getEffectiveInMarketDate(row);
    // The end date requires an in-market date on the same row (requires start), and only applies to
    // seasonal SKUs.
    const disabled = row?.legal === false || (!isReplenishable && !isOrderOnly) || !effectiveInMarketDate || !row.seasonal;
    // Show the effective date: the distributor's own override if set, otherwise the SKU's default.
    const value = toYyyyMmDdInput(row.endInMarketDateOverride) || itemEndInMarketDate;
    // No geography layer here, so the floor is simply this row's own in-market override.
    const min = effectiveInMarketDate;
    const weekLimit = maxInMarketDate();
    // Tightest upper bound: the item's own end in-market date and the 52-week limit (date strings sort chronologically).
    const max = [itemEndInMarketDate, weekLimit].filter(Boolean).sort()[0];

    const handleChange = async (newValue) => {
        let apiDate = null;
        if (newValue !== null) {
            if (!newValue.isValid()) return;
            const selected = newValue.format('YYYY-MM-DD');
            // Guard against typed input that bypasses the disabled calendar days.
            if (!isSunday(selected)) {
                showInMarketDateError('End in-market date must be a Sunday.');
                return;
            }
            if (min && selected < min) return;
            if (selected > max) return;
            apiDate = selected;
        }

        const updatedRow = {
            ...row,
            endInMarketDateOverride: apiDate ? `${apiDate}T00:00:00` : null
        };

        const result = await PutProductDistributorFulfillmentType(
            row.product,
            { ...updatedRow, id: row.distributorId, skuFulfillmentType: row.skuFulfillmentType },
            null,
            { setEndInMarketDate: true, endInMarketDate: apiDate }
        );
        if (result.ok) {
            await handleUpdateRow(updatedRow);
        } else {
            showInMarketDateError(result.error || 'The end in-market date could not be saved.');
        }
    };

    return (
        <DatePicker
            value={value ? dayjs(value) : null}
            disabled={disabled}
            onChange={handleChange}
            minDate={min ? dayjs(min) : undefined}
            maxDate={dayjs(max)}
            shouldDisableDate={(date) => date.day() !== 0}
            slotProps={{
                textField: { size: 'small', fullWidth: true },
                field: { clearable: true }
            }}
        />
    );
};

SkuEndInMarketDatePicker.propTypes = {
    row: PropTypes.object,
    handleUpdateRow: PropTypes.func
};
